import { useRef, useEffect } from 'react';
import { getImagePath } from '../utils/imagePath';
import { useContent } from '../context/ContentContext';
import EditableText from './EditableText';

const WHITE_STROKE_FILTER = 'drop-shadow(1px 0 0 #fff) drop-shadow(-1px 0 0 #fff) drop-shadow(0 1px 0 #fff) drop-shadow(0 -1px 0 #fff) drop-shadow(1px 1px 0 #fff) drop-shadow(-1px -1px 0 #fff) drop-shadow(1px -1px 0 #fff) drop-shadow(-1px 1px 0 #fff)';

const WHITE_STROKE_SOFT = 'drop-shadow(1px 0 0 rgba(255,255,255,0.52)) drop-shadow(-1px 0 0 rgba(255,255,255,0.52)) drop-shadow(0 1px 0 rgba(255,255,255,0.52)) drop-shadow(0 -1px 0 rgba(255,255,255,0.52)) drop-shadow(1px 1px 0 rgba(255,255,255,0.52)) drop-shadow(-1px -1px 0 rgba(255,255,255,0.52)) drop-shadow(1px -1px 0 rgba(255,255,255,0.52)) drop-shadow(-1px 1px 0 rgba(255,255,255,0.52))';

const TOWNHALL_LOGO_INDEX = 3;

const BottomBar = ({ onOpenTerms }) => {
  const { getText } = useContent();
  const logosRef = useRef([]);
  const animationFrameRef = useRef(null);
  const isAnimatingRef = useRef(false);
  const animationStartTimeRef = useRef(null);

  // Logo greyscale reveal animation when footer enters view
  useEffect(() => {
    const logos = logosRef.current.filter(Boolean);
    if (!logos.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const footerVisible = entries.some((e) => e.isIntersecting);
        if (!footerVisible || isAnimatingRef.current) return;

        const revealDuration = 2000;
        const resetLogos = () => {
          logos.forEach((logo, index) => {
            if (logo) {
              const stroke = index === TOWNHALL_LOGO_INDEX ? WHITE_STROKE_SOFT : WHITE_STROKE_FILTER;
              logo.style.filter = `grayscale(100%) ${stroke}`;
              logo.style.opacity = '0.7';
              logo.classList.remove('revealed');
            }
          });
        };

        const animateReveal = (timestamp) => {
          if (!animationStartTimeRef.current) animationStartTimeRef.current = timestamp;
          const elapsed = timestamp - animationStartTimeRef.current;
          const progress = Math.min(1, elapsed / revealDuration);

          logos.forEach((logo, index) => {
            if (!logo) return;
            const stroke = index === TOWNHALL_LOGO_INDEX ? WHITE_STROKE_SOFT : WHITE_STROKE_FILTER;
            const staggerDelay = index * 150;
            const adjustedElapsed = Math.max(0, elapsed - staggerDelay);
            const adjustedProgress = Math.min(1, adjustedElapsed / revealDuration);
            const grayscaleAmount = 100 - adjustedProgress * 100;
            const opacity = 0.7 + adjustedProgress * 0.3;
            logo.style.filter = `grayscale(${Math.max(0, Math.min(100, grayscaleAmount))}%) ${stroke}`;
            logo.style.opacity = Math.max(0.7, Math.min(1, opacity));
            if (adjustedProgress >= 0.5) logo.classList.add('revealed');
          });

          if (progress < 1) {
            animationFrameRef.current = requestAnimationFrame(animateReveal);
          } else {
            isAnimatingRef.current = false;
            animationStartTimeRef.current = null;
          }
        };

        resetLogos();
        isAnimatingRef.current = true;
        animationStartTimeRef.current = null;
        animationFrameRef.current = requestAnimationFrame(animateReveal);
      },
      { threshold: 0.1, rootMargin: '0px' }
    );

    const footer = document.getElementById('footer');
    if (footer) observer.observe(footer);
    return () => {
      observer.disconnect();
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const phone = getText('contact.phone') ?? '(800) 231-5629';
  const phoneTel = phone.replace(/\D/g, '');
  const email = getText('contact.email') ?? 'info@thevbcgroup.com';

  return (
    <footer
      id="footer"
      role="contentinfo"
      className="relative overflow-hidden bg-[#1a1a1a] text-[#fafafa] p-0 w-screen max-w-[100vw] box-border ml-[calc(50%-50vw)]"
    >
      {/* Wavy top border */}
      <div className="relative w-full h-14 -mt-px pointer-events-none" aria-hidden="true">
        <svg viewBox="0 0 1200 60" preserveAspectRatio="none" className="w-full h-full block align-top">
          <path d="M0,0 L0,30 Q150,50 300,30 T600,30 T900,30 T1200,30 L1200,0 Z" fill="#1a1a1a" />
        </svg>
      </div>

      <div className="px-4 md:px-6">
        <div className="max-w-[1200px] w-full mx-auto py-8 px-5 pb-6 sm:py-10 sm:px-6 md:py-12 md:px-8 md:pb-10 relative z-10 bg-[#1a1a1a] border-2 border-[#9a83ad] rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-12 md:gap-16 mb-12 bg-[#1a1a1a] items-start">
          {/* Left: Quick links */}
          <div className="min-w-0 text-center md:text-left">
            <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-accent-color mb-5 font-sans">
              Quick Links
            </h3>
            <ul className="list-none m-0 p-0 flex flex-wrap justify-center gap-x-4 gap-y-1 md:block md:flex-none">
              <li className="mb-2 md:mb-3">
                <a
                  href="#about"
                  onClick={(e) => scrollToSection(e, 'about')}
                  className="text-[0.9rem] text-white/90 no-underline transition-colors duration-200 hover:text-accent-color"
                >
                  <EditableText path="navbar.about" fallback="About Us" />
                </a>
              </li>
              <li className="mb-2 md:mb-3">
                <a
                  href="#services"
                  onClick={(e) => scrollToSection(e, 'services')}
                  className="text-[0.9rem] text-white/90 no-underline transition-colors duration-200 hover:text-accent-color"
                >
                  <EditableText path="navbar.services" fallback="Services" />
                </a>
              </li>
              <li className="mb-2 md:mb-3">
                <a
                  href="#pricing"
                  onClick={(e) => scrollToSection(e, 'pricing')}
                  className="text-[0.9rem] text-white/90 no-underline transition-colors duration-200 hover:text-accent-color"
                >
                  <EditableText path="navbar.pricing" fallback="Pricing" />
                </a>
              </li>
              <li className="mb-2 md:mb-3">
                <a
                  href="#team"
                  onClick={(e) => scrollToSection(e, 'team')}
                  className="text-[0.9rem] text-white/90 no-underline transition-colors duration-200 hover:text-accent-color"
                >
                  <EditableText path="navbar.meetTeam" fallback="Meet Our Team" />
                </a>
              </li>
              <li className="mb-2 md:mb-3">
                <a
                  href="#contact"
                  onClick={(e) => scrollToSection(e, 'contact')}
                  className="text-[0.9rem] text-white/90 no-underline transition-colors duration-200 hover:text-accent-color"
                >
                  <EditableText path="navbar.contact" fallback="Contact Us" />
                </a>
              </li>
              <li className="mb-2 md:mb-3">
                {onOpenTerms && (
                  <button
                    type="button"
                    onClick={onOpenTerms}
                    className="text-[0.9rem] text-white/90 bg-transparent border-none p-0 cursor-pointer text-left transition-colors duration-200 hover:text-accent-color font-inherit"
                  >
                    Terms and Conditions
                  </button>
                )}
              </li>
            </ul>
          </div>

          {/* Center: Brand */}
          <div className="min-w-0 text-center">
            <div className="text-xl font-semibold uppercase tracking-[0.08em] text-bg-white mb-2 font-serif">
              <EditableText path="navbar.companyName" fallback="The VBC Group" />
            </div>
            <p className="text-[0.95rem] text-white/95 m-0 mb-1.5 font-medium">
              <EditableText path="hero.tagline" fallback="Purpose driven messaging" />
            </p>
            <p className="text-[0.8rem] text-accent-color m-0 font-medium">
              <EditableText path="hero.subtitle" fallback="Texts · Calls · Townhalls" />
            </p>
            <img
              src={getImagePath('/images/logos/white_heart.png')}
              alt="The VBC Group"
              className="block w-full max-w-[160px] h-auto mt-5 mx-auto object-contain sm:max-w-[120px] sm:mt-4"
            />
          </div>

          {/* Right: Contact */}
          <div className="min-w-0 text-center md:text-right">
            <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-accent-color mb-5 font-sans">
              Contact
            </h3>
            <a
              href={`mailto:${email}`}
              className="block text-[0.9rem] text-white/90 no-underline mb-3 transition-colors duration-200 hover:text-accent-color"
            >
              {email}
            </a>
            <a
              href={`tel:${phoneTel}`}
              className="block text-[0.9rem] text-white/90 no-underline mb-3 transition-colors duration-200 hover:text-accent-color"
            >
              {phone}
            </a>
            <a
              href="#contact"
              onClick={(e) => scrollToSection(e, 'contact')}
              className="inline-block mt-4 py-2 px-4 text-[0.85rem] font-semibold uppercase tracking-wide text-[#1a1a1a] bg-bg-white rounded transition-colors duration-200 hover:bg-accent-color hover:text-bg-white no-underline"
            >
              <EditableText path="hero.cta" fallback="Get Started" />
            </a>
          </div>
        </div>

        {/* Partner logos row */}
        <div className="border-t border-[rgba(154,131,173,0.35)] pt-10 bg-[#1a1a1a]">
          <div className="grid grid-cols-2 gap-6 justify-items-center items-center sm:flex sm:flex-wrap sm:justify-center sm:gap-6 sm:gap-y-4 md:gap-8 md:gap-y-6">
            <a
              href="https://voicebroadcasting.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center no-underline text-inherit shrink-0"
            >
              <img
                ref={(el) => (logosRef.current[0] = el)}
                src={getImagePath('/images/footerLogos/VBlogo.png')}
                alt="Voice Broadcasting"
                className="h-auto max-h-14 min-h-8 w-auto max-w-[150px] object-contain opacity-75 grayscale transition-all duration-300 hover:grayscale-0 hover:opacity-100 hover:scale-105 [&.revealed]:grayscale-0 [&.revealed]:opacity-100 sm:max-h-11 sm:max-w-[110px] md:max-h-16 md:max-w-[170px]"
                style={{ filter: `grayscale(100%) ${WHITE_STROKE_FILTER}` }}
              />
            </a>
            <a
              href="https://pollmakers.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center no-underline text-inherit shrink-0"
            >
              <img
                ref={(el) => (logosRef.current[1] = el)}
                src={getImagePath('/images/footerLogos/pollmakers transparent background.png')}
                alt="Pollmakers"
                className="h-auto max-h-14 min-h-8 w-auto max-w-[150px] object-contain opacity-75 grayscale transition-all duration-300 hover:grayscale-0 hover:opacity-100 hover:scale-105 [&.revealed]:grayscale-0 [&.revealed]:opacity-100 sm:max-h-11 sm:max-w-[110px]"
                style={{ filter: `grayscale(100%) ${WHITE_STROKE_FILTER}` }}
              />
            </a>
            <a
              href="https://www.texttosurvey.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center no-underline text-inherit shrink-0"
            >
              <img
                ref={(el) => (logosRef.current[2] = el)}
                src={getImagePath('/images/footerLogos/texttosurvey_logoonly.png')}
                alt="Text to Survey"
                className="h-auto max-h-14 min-h-8 w-auto max-w-[150px] object-contain opacity-75 grayscale transition-all duration-300 hover:grayscale-0 hover:opacity-100 hover:scale-105 [&.revealed]:grayscale-0 [&.revealed]:opacity-100 sm:max-h-11 sm:max-w-[110px]"
                style={{ filter: `grayscale(100%) ${WHITE_STROKE_FILTER}` }}
              />
            </a>
            <a
              href="https://townhallmeetings.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center no-underline text-inherit shrink-0"
            >
              <img
                ref={(el) => (logosRef.current[3] = el)}
                src={getImagePath('/images/footerLogos/TownhallLongOrange.png')}
                alt="Townhalls"
                className="h-auto max-h-14 min-h-8 w-auto max-w-[150px] object-contain opacity-75 grayscale transition-all duration-300 hover:grayscale-0 hover:opacity-100 hover:scale-105 [&.revealed]:grayscale-0 [&.revealed]:opacity-100 sm:max-h-[54px] sm:max-w-[140px] md:max-h-[68px] md:max-w-[190px]"
                style={{ filter: `grayscale(100%) ${WHITE_STROKE_SOFT}` }}
              />
            </a>
          </div>
        </div>
      </div>
      </div>
    </footer>
  );
};

export default BottomBar;
